<app-navbar [home_address]="session_info?.last_routes.new_home || '/'"></app-navbar>
<div class="contact-header">
  <p class="text-white display-3">Automate your QR Code generation with our API</p>
  <p class="text-white h4 mt-4">Create numerous Static QR Codes, and integrate them into your application with your
    choice of branded styles.</p>
  <a class="btn btn-secondary-light mt-4"
    [routerLink]="coreService.isLoggedIn() ? '/user/account-settings' : '/auth/signup'">
    <i class="bi bi-key me-2"></i>
    Get Started
  </a>
</div>
<div class="api-documentation-wrapper">
  <div class="container">

    <p class="mt-5">With <code>BrandQrCodes API</code>, we've made it easy to generate and integrate your branded QR
      codes into
      your app or company's workflow. This API can be used in almost any scenario where real-time QR code
      generation is required, most common use cases are ticket generation or visitor's pass, coupons, web page
      visits e.t.c.
    </p>

    <div class="row qr-preview-wrapper">
      <div class="col-md-3 col-sm-6 col-lg-3 p-2 text-center">
        <img loading="lazy" src="assets/api-documentation/twitter-brandqrcodes.png" />
      </div>
      <div class="col-md-3 col-sm-6 col-lg-3 p-2 text-center">
        <img loading="lazy" src="assets/api-documentation/ig-brandqrcodes.png" />
      </div>
      <div class="col-md-3 col-sm-6 col-lg-3 p-2 text-center">
        <img loading="lazy" src="assets/api-documentation/facebook-brandqrcodes.png" />
      </div>
      <div class="col-md-3 col-sm-6 col-lg-3 p-2 text-center">
        <img loading="lazy" src="assets/api-documentation/brandqrcodes.com.png" />
      </div>
    </div>

    <h4 class="my-3">Getting Started</h4>
    <p>
      To get started, you'll need a <code>BrandQrCodes</code> account. API Keys can be generated and managed from
      your account settings page. Copy the generated API Key and include it in the query string parameter of all
      your requests.
    </p>

    <div class="alert alert-info d-flex align-items-center animated fadeIn mb-5" role="alert">
      <div class="bi bi-exclamation-circle-fill"></div>
      <div><strong>NOTE</strong>: To prevent abuse of your API keys, you can whitelist a set of public IP
        addresses from where you'll be making your requests, all requests outside these set of IPs will be
        rejected.</div>
    </div>

    <h4 class="my-3">Generate QR code</h4>
    <div class="bg-light border rounded p-2">
      <strong class="me-1"><code>POST</code></strong> Create QR Code
    </div>

    <p class="mt-3 mb-5">
      <code>https://api.brandqrcodes.com/v1/qr-code/generate?key=example-api-key</code>
    </p>
    <h6>Request Body</h6>
    <div class="border rounded pt-3 ps-3">
      <pre>{{request_param_example_1 | json}}</pre>
    </div>
    <hr class="my-5">
    <div class="bg-light border rounded p-2">
      <strong class="me-1"><code>GET</code></strong> Create QR Code
    </div>

    <p class="mt-3 mb-3">
      Alternatively, you can as well use <code>GET</code> method with the same request parameters in the query
      string to generate QR codes.
    </p>
    <p>
      <code>https://api.brandqrcodes.com/v1/qr-code/generate?key=example-api-key&text=brandqrcodes.com&logo=default&save=false&margin=1.5&dot_color=%23EF5454&background_color=%23FBD3D4</code>
    </p>
    <div class="alert alert-info d-flex align-items-center animated fadeIn mb-5" role="alert">
      <div class="bi bi-exclamation-circle-fill"></div>
      <div><strong>NOTE</strong>: Because some characters cannot be part of a URL (for example, the space) and
        some other characters have a special meaning in a URL. We recommend you to use your platform's normal
        URL building libraries to automatically encode your URLs to deal with this problem. IPs will be
        rejected.</div>
    </div>
    <hr class="my-5">

    <div class="table-responsive animated fadeIn">
      <table class="table table-striped">
        <thead>
          <tr>
            <th scope="col">Property</th>
            <th scope="col">Required</th>
            <th scope="col">Data Type</th>
            <th scope="col">Remark</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-success">text</td>
            <td>Yes</td>
            <td>String</td>
            <td>The text to be encoded. <br>
              Note that the maximum text length is <code>300</code></td>
          </tr>
          <tr>
            <td class="text-success">margin</td>
            <td>No</td>
            <td>Number (<code>0</code> - <code>2</code>)</td>
            <td>The width of the white border around the data portion of the
              code. This value should be between 0 and 2.</td>
          </tr>
          <tr>
            <td class="text-success">save</td>
            <td>No</td>
            <td>Boolean (<code>true</code>, <code>false</code>)</td>
            <td>Pass <code>true</code> to save this QR code in your dashboard.</td>
          </tr>
          <tr>
            <td class="text-success">background_color</td>
            <td>No</td>
            <td>String</td>
            <td>Specify the background color for QR Code. <br>
              Hex value in short format (#[a-fA-F] three chars, e.g. fff for
              white) or long format (#[a-fA-F] six chars, e.g. <code>#ff0000</code> for red)
              <br>
              <br>
              The default color is black (<code>#000000</code>)
            </td>
          </tr>
          <tr>
            <td class="text-success">dot_color</td>
            <td>No</td>
            <td>String</td>
            <td>Specify the dot color for QR Code. <br>
              Hex value in short format (#[a-fA-F] three chars, e.g. fff for
              white) or long format (#[a-fA-F] six chars, e.g. <code>#ff0000</code> for red)
              <br>
              <br>
              The default color is black (<code>#000000</code>)
            </td>
          </tr>
          <tr>
            <td class="text-success">logo</td>
            <td>No</td>
            <td>
              <ng-container *ngFor="let item of session_info.resources.qr_logos">
                <code>{{item.id}}</code>,
              </ng-container>
              <code>default</code>
            </td>
            <td>The middle logo in the QR code.
              No logo will be set if this property is missing in the request
              body. <br> Passing <code>default</code> as a value uses your account’s default
              QR logo.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row bg-light m-0 mt-4 icon-wrapper">
      <div class="col-md-2 p-3" *ngFor="let item of session_info.resources.qr_logos">
        <img loading="lazy" [src]="item.url" class="float-start me-2" width="30" height="30" />
        <div><code>{{item.id}}</code></div>
      </div>
      <ng-container *ngIf="getDefaultLogo(); let logo">
        <div class="col-md-2 p-3">
          <img loading="lazy" [src]="logo.url" class="float-start me-2" width="30" height="30" />
          <div><code>default</code></div>
        </div>
      </ng-container>
    </div>

    <hr class="my-5">
    <h5>Example Request 1</h5>
    <div class="bg-light pt-3 ps-3 pe-3 pb-4 mt-4 request-wrapper">
      <code>
                <pre>curl --location --request POST <strong class="text-success">'https://api.brandqrcodes.com/v1/qr-code/generate?key=example-api-key'</strong> \
--data-raw '{{'{'}}
    <span class="text-success">"text"</span>: <span class="text-success">"brandqrcodes.com"</span>,
    <span class="text-success">"logo"</span>: <span class="text-success">"unknown-logo"</span>
{{'}'}}'
                </pre>
            </code>

      <h6>Response</h6>
      <div class="bg-light pt-3 ps-3 border request-wrapper-response">
        <pre>
{{'{'}}
    <span class="text-success">"success"</span>: <code>false</code>,
    <span class="text-success">"error"</span>: <span class="text-success">"Unknown logo value"</span>,
{{'}'}}
</pre>
      </div>
    </div>


    <h5 class="mt-5">Example Request 2</h5>
    <div class="bg-light pt-3 ps-3 pe-3 pb-4 mt-4 request-wrapper">
      <code>
                <pre>curl --location --request POST <strong class="text-success">'https://api.brandqrcodes.com/v1/qr-code/generate?key=example-api-key'</strong> \
--data-raw '{{'{'}}
    <span class="text-success">"text"</span>: <span class="text-success">"brandqrcodes.com"</span>,
    <span class="text-success">"logo"</span>: <span class="text-success">"scan-me"</span>
{{'}'}}'
                </pre>
            </code>

      <h6>Response</h6>
      <div class="bg-light p-3 border request-wrapper-response">
        <img loading="lazy" src="assets/api-documentation/scan-me-brandqrcodes.com.png" width="300" height="300" />
      </div>
    </div>

    <div class="text-center mt-5">
      <br>
      <br>
      <p>
        If you require a custom solution for a specific need or a big corporate application, please feel free to
        contact us.
      </p>

      <a class="btn btn-secondary-light" routerLink="/contact-us">
        Contact us
      </a>
    </div>

  </div>
</div>
<app-footer></app-footer>